import React, { useRef, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CameraIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const WebcamCaptureModal = ({ isOpen, onClose, onCapture }) => {
    const videoRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            startCamera();
        } else {
            stopCamera();
        }
        return () => stopCamera();
    }, [isOpen]);

    const startCamera = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ 
                video: { facingMode: 'user' }, 
                audio: false 
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (err) {
            console.error("Error accessing webcam:", err);
            setError("Could not access camera. Please check permissions.");
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current) return;
        
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoRef.current, 0, 0);
        
        canvas.toBlob((blob) => {
            const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
            onCapture(file);
            onClose();
        }, 'image/jpeg', 0.8);
    };

    return (
        <Transition.Root show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-3xl bg-slate-900 text-left shadow-2xl transition-all w-full max-w-lg border border-white/10">
                                <div className="absolute top-4 right-4 z-10">
                                    <button onClick={onClose} className="p-2 bg-black/40 text-white rounded-full hover:bg-black/60 transition-colors">
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="p-1">
                                    {error ? (
                                        <div className="aspect-video flex flex-col items-center justify-center text-white p-8 text-center">
                                            <div className="w-16 h-16 bg-red-500/20 text-red-500 rounded-full flex items-center justify-center mb-4">
                                                <XMarkIcon className="w-8 h-8" />
                                            </div>
                                            <p className="font-bold">{error}</p>
                                            <button onClick={startCamera} className="mt-4 px-6 py-2 bg-white text-black rounded-xl font-bold">Try Again</button>
                                        </div>
                                    ) : (
                                        <div className="relative aspect-video bg-black rounded-[1.25rem] overflow-hidden">
                                            <video 
                                                ref={videoRef} 
                                                autoPlay 
                                                playsInline 
                                                className="w-full h-full object-cover"
                                            />
                                            {!stream && (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <ArrowPathIcon className="w-10 h-10 text-white animate-spin opacity-50" />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="p-8 flex justify-center bg-slate-900">
                                    <button
                                        onClick={capturePhoto}
                                        disabled={!stream}
                                        className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl active:scale-90 transition-transform disabled:opacity-50 disabled:scale-100 group"
                                    >
                                        <div className="w-16 h-16 rounded-full border-4 border-slate-900 group-hover:scale-95 transition-transform"></div>
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default WebcamCaptureModal;
